import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Stack,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../services/authService';
import { clearUser } from '../userSlice';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import MeetingLogo from "../assets/Meet-ing-logo.jpg";

export default function OnboardingHeader() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const theme = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(clearUser());
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Helper functions for user information
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.name) return user.name;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.username) return user.username;
    if (user.email) return user.email.split('@')[0];
    return 'User';
  };

  const getUserAvatar = () => {
    if (!user) return null;
    return user.avatarUrl || user.avatar?.url || user.avatar?.thumbnailUrl || null;
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const displayName = getUserDisplayName();
    if (displayName === 'User' && user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  };

  return (
    <Box>
      <AppBar 
        position="fixed" 
        color="default" 
        elevation={0} 
        sx={{ 
          height: "73px", 
          zIndex: theme.zIndex.appBar,
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(8px)',
          transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ height: "100%", px: 2, py: 3 }}>
          {/* Left Section - Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Box component="img" src={MeetingLogo} alt="Meeting logo" sx={{ width: 128, height: 47 }} />
          </Box>

          {/* Center Section - Organization Info */}
          {user?.currentOrganization && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mx: 4 }}>
              <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {user.currentOrganization.name}
              </Typography>
            </Box>
          )}

          {/* Right Section - User Info & Logout */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* User Info */}
            {user && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  src={getUserAvatar()}
                  sx={{
                    width: 33,
                    height: 33,
                    bgcolor: "#ed5191",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {getUserInitials()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {getUserDisplayName()}
                  </Typography>
                </Box>
              </Stack>
            )}
            
            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outlined"
              startIcon={<LogoutIcon />}
              sx={{
                borderRadius: "3px",
                textTransform: "none",
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
                fontSize: "14px",
                px: 3,
                py: 1,
                borderColor: theme.palette.error.main,
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.light,
                  borderColor: theme.palette.error.main,
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Spacer to push content below fixed header */}
      <Box sx={{ height: "73px" }} />
    </Box>
  );
} 